import ErrorBoundary from '../__components/ErrorBoundary';
import LayoutChooser from '@base/components/src/LayoutChooser';
import { NextAppWithPageLayoutProps, PageLayoutGetter } from '@base/components/src/LayoutChooser/types';
import NeedleScript from '@base/components/src/Scripts/NeedleScript';
import AwsRumScript from '@base/components/src/Scripts/AwsRumScript';
import MarketplaceScriptHandler from '@base/components/src/MarketplaceScriptHandler';
import ContentApiService from '@package/core/src/content/ContentApiService';
import ContentInteractor from '@package/core/src/content/ContentInteractor';
import TopBannerApiService from '@package/core/src/content/TopBannerApiService';
import { apiClient } from '@package/hooks/src/services';
import StringHTMLBuilder from '@package/services/src/StringHTMLBuilder';
import config from 'config-base';
import App, { AppContext } from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { PropsWithChildren, useState } from 'react';
import { DehydratedState, QueryClient, QueryClientProvider, Hydrate } from 'react-query';
import { LabelVariantsContext } from '@package/hooks/src/useLabelVatiants/labelVariantsContext';

import 'pure-react-carousel/dist/react-carousel.es.css';
import '../../styles/global.css';
import '../../styles/fonts.css';

type QueryProviderProps = {
  queryClient: QueryClient;
  dehydratedState?: DehydratedState;
};

function QueryProvider({ children, queryClient, dehydratedState }: PropsWithChildren<QueryProviderProps>) {
  return (
    <QueryClientProvider client={queryClient}>
      <Hydrate state={dehydratedState}>{children}</Hydrate>
    </QueryClientProvider>
  );
}

type AppProps = {
  cssData?: string;
  bannerMessage?: string;
  dehydratedState: DehydratedState;
  renderLayout: PageLayoutGetter;
  labels?: any;
};

const qc = new QueryClient();

function ShoppingCartApp({ Component, pageProps }: NextAppWithPageLayoutProps<AppProps>) {
  const [queryClientState] = useState(() => qc);

  const { cssData } = pageProps;
  const router = useRouter();
  return (
    <>
      <Head>
        {cssData && (
          <style id="head-styles" dangerouslySetInnerHTML={{ __html: cssData.replace(/<\/?[^>]+(>|$)/g, '') }}></style>
        )}

        <link rel="shortcut icon" href={`/marketplace-static/images/favicons/${config.brandCode === '2' ? 'agd' : 'pyr'}/favicon.ico`} type="image/x-icon" />
        <link rel="shortcut" href={`/marketplace-static/images/favicons/${config.brandCode === '2' ? 'agd' : 'pyr'}/favicon-32x32.png`} type="image/png" />
        <link rel="apple-touch-icon" href={`/marketplace-static/images/favicons/${config.brandCode === '2' ? 'agd' : 'pyr'}/apple-touch-icon.png`} sizes="180x180" />
      </Head>

      <QueryProvider queryClient={queryClientState} dehydratedState={pageProps.dehydratedState}>
        <ErrorBoundary>
          <LabelVariantsContext.Provider value={pageProps?.labels}>
            <LayoutChooser keyId="ssr" pageProps={pageProps} renderLayout={Component.renderLayout}>
              <Component {...pageProps} key={router.asPath} />
            </LayoutChooser>
          </LabelVariantsContext.Provider>
          <NeedleScript key="needle-analytics-script" />

          <MarketplaceScriptHandler>
            <>
              {config.awsRum === 'ON' && (
                <AwsRumScript
                  rumAppId={config.rumAppId}
                  rumGuestRoleArn={config.rumGuestRoleArn}
                  rumIdentityPoolId={config.rumIdentityPoolId}
                />
              )}
            </>
          </MarketplaceScriptHandler>

        </ErrorBoundary>
      </QueryProvider>
    </>
  );
}

ShoppingCartApp.getInitialProps = async (ctx: AppContext) => {
  const appProps = await App.getInitialProps(ctx);
  const customCSService = new ContentApiService(apiClient);
  const cssInteractor = new ContentInteractor(customCSService, StringHTMLBuilder);
  const cssData = await cssInteractor.getPageStyleCSS();

  let bannerMessage;
  if (ctx.ctx.pathname === '/shopping-cart') {
    const topBannerService = new TopBannerApiService(apiClient);
    const { data } = await topBannerService.getTopBannerContent();
    bannerMessage = data;
  }

  return {
    pageProps: { ...appProps.pageProps, cssData, bannerMessage },
  };
};

export default ShoppingCartApp;
